import React, { useEffect, useState } from 'react';
import { ArrowLeft, Play, Eye, Heart, MessageSquare, CheckCircle2, Check } from 'lucide-react';
import { supabase } from '../../../supabaseClient';

import { isVideoWatched, toggleWatchedStatus } from './tabs/watchedManager';

const formatCount = (count?: number) => {
  if (!count) return '0';
  if (count >= 1000000) return (count / 1000000).toFixed(1).replace('.', ',') + 'M';
  if (count >= 1000) return (count / 1000).toFixed(1).replace('.', ',') + ' mil';
  return count.toString();
};

export default function CategoryDetailView({ categoryTitle, initialVideos, onBack, onSelectVideo }: { categoryTitle: string, initialVideos?: any[], onBack: () => void, onSelectVideo: (id: string) => void }) {
  // Se recebeu a lista da prateleira, fatiamos ela de 10 em 10 para não pesar
  // 🛡️ Filtra para ignorar shorts nesta tela de categorias normais
  const filteredInitial = initialVideos ? initialVideos.filter(v => !(v.post_type === 'shorts' || v.is_short === true || (v.category || '').toLowerCase().includes('shorts'))) : [];
  const [displayLimit, setDisplayLimit] = useState(12); 
  const currentVideos = filteredInitial.slice(0, displayLimit);
  
  const [videos, setVideos] = useState<any[]>(currentVideos);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);

  const [loadedImages, setLoadedImages] = useState<{ [key: string]: boolean }>({});
  
  // Controla se ainda há mais itens para mostrar no botão "Carregar mais"
  const [hasMore, setHasMore] = useState(
    initialVideos ? initialVideos.length > 10 : true
  );
  const PAGE_SIZE = 10;

  // Adicionar para forçar atualização ao marcar/desmarcar
  const [, setForceUpdate] = useState(0);
  useEffect(() => {
    const handleWatchedChange = () => setForceUpdate(prev => prev + 1);
    window.addEventListener('doisplay_watched_updated', handleWatchedChange);
    return () => window.removeEventListener('doisplay_watched_updated', handleWatchedChange);
  }, []);

  const fetchVideos = async (pageToFetch: number, isSilent = false) => {
    if (!isSilent) setLoading(true);
    
    const from = pageToFetch * PAGE_SIZE;
    const to = from + PAGE_SIZE - 1;

    let query = supabase.from('videos').select('*').range(from, to);

    query = query.neq('post_type', 'shorts').neq('is_short', true);

    const titleLower = categoryTitle.toLowerCase();

    // 🛡️ O DETETOR DE AMBIENTE MUDOU DE SÍTIO!
    // Agora fica de fora do "if", ou seja, protege TODAS as listas (inclusive o "Mais Assistidos")
    const currentPath = window.location.pathname.toLowerCase();
    if (currentPath.includes('/games')) {
      query = query.ilike('category', '%game%');
    } else if (currentPath.includes('/filmes')) {
      query = query.not('category', 'ilike', '%game%').not('category', 'ilike', '%jogo%');
    }

    if (!titleLower.includes('todos') && !titleLower.includes('assistidos')) {
      let searchKeyword = '';
      
      if (titleLower.includes('terror') || titleLower.includes('suspense')) searchKeyword = 'terror';
      else if (titleLower.includes('ação') || titleLower.includes('acao')) searchKeyword = 'acao';
      else if (titleLower.includes('aventura')) searchKeyword = 'aventura';
      else if (titleLower.includes('desenho') || titleLower.includes('animação')) searchKeyword = 'desenho';
      else if (titleLower.includes('ficção') || titleLower.includes('ficcao')) searchKeyword = 'ficcao';
      else if (titleLower.includes('comédia') || titleLower.includes('comedia')) searchKeyword = 'comedia';
      else if (titleLower.includes('trailer')) searchKeyword = 'trailer'; 
      else if (titleLower.includes('game') || titleLower.includes('jogo')) searchKeyword = 'games'; 
      else {
        searchKeyword = categoryTitle.replace(/[^a-zA-ZÀ-ÿ0-9]/g, '').trim().toLowerCase();
      }

      if (searchKeyword) {
        query = query.ilike('category', `%${searchKeyword}%`);
      }
    }

    // 🚀 BÓNUS DE MESTRE: Se for a aba "Mais Assistidos" ou "Top", ordena pelas visualizações!
    // Senão, ordena pelos mais recentes.
    let data, error;
    if (titleLower.includes('assistidos') || titleLower.includes('top')) {
      const res = await query.order('views_count', { ascending: false });
      data = res.data;
      error = res.error;
    } else {
      const res = await query.order('created_at', { ascending: false });
      data = res.data;
      error = res.error;
    }

    if (error) {
      console.error('Erro ao buscar vídeos paginados:', error);
    } else if (data) {
      setHasMore(data.length >= PAGE_SIZE);
      
      setVideos(prev => {
        if (pageToFetch === 0) return data;
        const novosVideos = data.filter(novo => !prev.some(antigo => antigo.id === novo.id));
        return [...prev, ...novosVideos];
      });
    }
    if (!isSilent) setLoading(false);
  };

  // 🚀 AJUSTE CIRÚRGICO: O UseEffect agora faz a busca silenciosa
  useEffect(() => {
    if (initialVideos && initialVideos.length > 0) {
      setVideos(initialVideos); 
      setPage(0); 
      // A MÁGICA: O utilizador vê a tela na hora, mas o código vai alinhar a 
      // página 0 nos bastidores para que o clique no "Carregar Mais" não falhe!
      fetchVideos(0, true);
    } else {
      fetchVideos(0);
    }
  }, [categoryTitle, initialVideos]);

  const loadMore = () => {
    const nextPage = page + 1;
    setPage(nextPage);
    fetchVideos(nextPage);
  };

  return (
  <div style={{ backgroundColor: '#000000', minHeight: '100vh', color: '#fff', padding: '20px 0px', boxSizing: 'border-box', width: '100%' }}>
    
    {/* Cabeçalho com margem lateral para o título e botão de voltar não grudarem na borda extrema */}
    <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '20px', marginTop: '20px', padding: '0 12px' }}>
      <button 
        onClick={onBack}
        style={{
          display: 'flex', alignItems: 'center', gap: '8px',
          backgroundColor: 'rgba(255,255,255,0.1)', color: '#fff', border: 'none',
          padding: '8px 14px', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold'
        }}
      >
        <ArrowLeft size={18} /> Voltar
      </button>
      <h1 style={{ fontSize: '20px', fontWeight: '900', margin: 0 }}>{categoryTitle}</h1>
    </div>

    {/* Grade de Cards encostada nas pontas e maior */}
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '4px', width: '100%' }} className="category-grid">
<style>{`
  .category-grid {
    padding: 0 4px; /* Espacinho mínimo nas bordas laterais para não colar na carcaça do celular */
  }
  @media (min-width: 768px) {
    .category-grid {
      grid-template-columns: repeat(auto-fill, minmax(240px, 1fr)) !important;
      gap: 15px !important;
      padding: 0 20px !important;
    }
  }

  @keyframes shimmer {
    0% { background-position: -200% 0; }
    100% { background-position: 200% 0; }
  }
  .image-skeleton {
    background: linear-gradient(90deg, #1a1a1a 25%, #2a2a2a 50%, #1a1a1a 75%);
    background-size: 200% 100%;
    animation: shimmer 1.5s infinite;
  }
`}</style>
      {videos.map((video) => (
        <div 
          key={video.id}
          onClick={() => {
            onBack(); 
            onSelectVideo(video.id); 
          }}
          style={{ cursor: 'pointer', backgroundColor: '#141414', borderRadius: '4px', overflow: 'hidden', border: 'none' }}
        >
         <div style={{ width: '100%', aspectRatio: '16/9', position: 'relative', backgroundColor: '#141414', overflow: 'hidden' }}>
            
            {/* Fundo cinza animado (Skeleton) enquanto a imagem baixa */}
            {!loadedImages[video.id] && (
              <div className="image-skeleton" style={{ position: 'absolute', inset: 0, zIndex: 1 }} />
            )}

            <img 
              src={video.thumbnail} 
              alt={video.title} 
              loading="lazy"
              decoding="async"
              onLoad={() => setLoadedImages(prev => ({ ...prev, [video.id]: true }))}
              style={{ 
                width: '100%', 
                height: '100%', 
                objectFit: 'cover',
                opacity: loadedImages[video.id] ? 1 : 0,
                transition: 'opacity 0.3s ease-in-out',
                position: 'relative',
                zIndex: 2
              }} 
            />

{/* BOTÃO DE MARCAR COMO ASSISTIDO */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                toggleWatchedStatus(video.id);
              }}
              title={isVideoWatched(video.id) ? "Marcar como não assistido" : "Marcar como assistido"}
              style={{
                position: 'absolute',
                top: '6px',
                left: '6px',
                backgroundColor: isVideoWatched(video.id) ? '#22c55e' : 'rgba(0, 0, 0, 0.45)',
                color: '#2e2b2b',
                border: 'none',
                borderRadius: '4px',
                width: '15px',
                height: '15px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                zIndex: 5,
                backdropFilter: 'blur(4px)',
                transition: 'background 0.2s'
              }}
            >
              {isVideoWatched(video.id) ? <CheckCircle2 size={16} color="#fff" /> : <Check size={14} color="#2a2727" />}
            </button>

            {/* 🟢 ETIQUETA DE TIPO NA CAPA (Trailer vs Filme) */}
            {/* 🟢 ETIQUETA DE TIPO NA CAPA (Trailer vs Game vs Filme) */}
          <div style={{ 
            position: 'absolute', 
            top: '6px', /* No CategoryRow pode ser 8px */
            right: video.is_premium ? '42px' : '6px', /* Ajuste conforme o arquivo */
            backgroundColor: video.category?.toLowerCase().includes('trailer') ? 'rgba(229, 9, 20, 0.9)' : 'rgba(0, 0, 0, 0.85)', 
            color: '#fff', 
            fontSize: '8px', 
            fontWeight: 'bold', 
            padding: '2px 5px', 
            borderRadius: '3px', 
            zIndex: 4,
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            border: '1px solid rgba(255,255,255,0.15)'
          }}>
            {video.category?.toLowerCase().includes('trailer') 
              ? '🎬 Trailer' 
              : video.category?.toLowerCase().includes('game') 
                ? '🎮 Game' 
                : '🎥 Filme'}
          </div>

            {video.is_premium && (
              <span style={{ position: 'absolute', top: '6px', right: '6px', backgroundColor: '#E50914', color: '#fff', fontSize: '10px', padding: '2px 6px', borderRadius: '3px', fontWeight: 'bold', zIndex: 4 }}>
                VIP
              </span>
            )}
          </div>
          <div style={{ padding: '10px 8px' }}>
            <h4 style={{ fontSize: '13px', fontWeight: '600', margin: '0 0 6px 0', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', color: '#fff' }}>
              {video.title}
            </h4>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '11px', color: '#888' }}>
              
              <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}><Eye size={12} /> {formatCount(video.views_count ?? 0)}</span>
              <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}><Heart size={12} color="#E50914" /> {formatCount(video.likes_count ?? 0)}</span>
              <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}><MessageSquare size={12} color="#3b82f6" /> {formatCount(video.comments_count ?? 0)}</span>
            
            </div>
          </div>
        </div>
      ))}
    </div>

    {hasMore && (
      <div style={{ textAlign: 'center', marginTop: '30px', paddingBottom: '40px' }}>
        <button 
          onClick={loadMore}
          disabled={loading}
          style={{
            backgroundColor: '#E50914', color: '#fff', border: 'none',
            padding: '12px 30px', borderRadius: '4px', fontWeight: 'bold', fontSize: '15px', cursor: 'pointer',
            opacity: loading ? 0.7 : 1
          }}
        >
          {loading ? 'Carregando...' : 'Carregar mais (mais vídeos)'}
        </button>
      </div>
    )}

  </div>
);
}